import { createTheme, Zoom } from '@mui/material'

const sfPro = 'SF Pro, sans-serif'

export const theme = createTheme({
  palette: {
    primary: {
      main: '#6100DC',
      light: '#EDEDF8',
    },
    success: {
      main: '#00AB75',
    },
    text: {
      primary: '#232343',
      secondary: '#7B7B8E',
      disabled: '#A2A2AF',
    },
    background: {
      default: '#F9FAFC',
    },
    divider: '#EDEDF8',
    error: {
      main: '#FF3333',
      light: '#FFD6D6',
    },
    info: {
      main: '#31A4FF',
    },
    warning: {
      main: '#FFAB00',
      light: '#FFE8B8',
    },
    custom: {
      main: '#FFF',
    },
    common: {
      white: '#FFF',
    },
    alabaster: '#FAFAFA',
    carribeanGreen: '#00CB8B',
    moonRaker: '#DDC9F7',
  },

  components: {
    MuiPaper: {
      defaultProps: {
        variant: 'outlined',
      },
      variants: [
        {
          props: { variant: 'elevation' },
          style: {
            boxShadow:
              '0px 1px 1px rgba(0, 0, 0, 0.05), 0px 1px 4px rgba(50, 50, 105, 0.16)',
          },
        },
      ],
    },
  },
})

const { pxToRem } = theme.typography

theme.typography = {
  ...theme.typography,

  h1: {
    fontFamily: sfPro,
    fontSize: pxToRem(32.43),
    lineHeight: pxToRem(51.89),
    letterSpacing: '0.1px',
  },

  h2: {
    fontFamily: sfPro,
    fontSize: pxToRem(28.83),
    lineHeight: pxToRem(46.13),
    letterSpacing: '0.1px',
  },

  h3: {
    fontFamily: sfPro,
    fontSize: pxToRem(25.63),
    lineHeight: pxToRem(41.01),
    letterSpacing: '0.1px',
  },

  body1: {
    fontFamily: sfPro,
    fontSize: pxToRem(22.78),
    lineHeight: pxToRem(36.45),
    letterSpacing: '0.1px',
  },

  body2: {
    fontFamily: sfPro,
    fontSize: pxToRem(20.25),
    lineHeight: pxToRem(32.4),
    letterSpacing: '0.1px',
  },

  body3: {
    fontFamily: sfPro,
    fontSize: pxToRem(18),
    lineHeight: pxToRem(28.8),
    letterSpacing: '0.1px',
  },

  caption1: {
    fontFamily: sfPro,
    fontSize: pxToRem(16),
    lineHeight: pxToRem(25.6),
    letterSpacing: '0.1px',
  },

  caption2: {
    fontFamily: sfPro,
    fontSize: pxToRem(14.22),
    lineHeight: pxToRem(22.75),
    letterSpacing: '0.1px',
  },

  caption3: {
    fontFamily: sfPro,
    fontSize: pxToRem(12.64),
    lineHeight: pxToRem(20.22),
    letterSpacing: '0.1px',
  },

  button: {
    fontFamily: sfPro,
    fontSize: pxToRem(14),
    lineHeight: pxToRem(25),
    fontWeight: 600,
    letterSpacing: '0.1px',
  },
}

theme.components = {
  ...theme.components,

  MuiTypography: {
    styleOverrides: {
      root: {
        display: 'inline-block',
      },
    },
  },

  MuiIconButton: {
    defaultProps: {
      disableRipple: true,
    },

    styleOverrides: {
      root: {
        padding: 0,

        '&.Mui-disabled': {
          color: theme.palette.text.disabled,
        },
      },
    },
  },

  MuiTooltip: {
    defaultProps: {
      TransitionComponent: Zoom,
    },
    styleOverrides: {
      tooltip: {
        lineHeight: '24px',
        padding: theme.spacing(0, 1),
        margin: 0,
        maxWidth: 'fit-content',
      },
    },
  },

  MuiToolbar: {
    defaultProps: {
      disableGutters: true,
    },
    styleOverrides: {
      root: {
        height: '56px',
        flex: 'none',
        background: theme.palette.common.white,
        display: 'flex',
        alignItems: 'center',
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: { borderBottomWidth: '2px' },
    },
  },

  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        minWidth: 'auto',
        padding: theme.spacing(1, 2),
        height: theme.spacing(5),
        borderRadius: '4px',
      },
    },
    variants: [
      {
        props: { variant: 'text' },
        style: {
          color: theme.palette.text.primary,

          '&:hover': {
            background: theme.palette.primary.light,
          },

          '&:focus': {
            color: theme.palette.info.main,
          },

          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
          },
        },
      },

      {
        props: { variant: 'contained' },
        style: {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.common.white,
          border: theme.palette.primary.light,
          boxShadow:
            '0px 2px 1px rgba(0, 0, 0, 0.05), 0px 0px 1px rgba(0, 0, 0, 0.25)',

          '&:focus': {
            border: theme.palette.info.main,
            boxShadow:
              '0px 2px 1px rgba(0, 0, 0, 0.05), 0px 0px 1px rgba(0, 0, 0, 0.25)',
          },

          '&.Mui-disabled': {
            backgroundColor: theme.palette.alabaster,
            color: theme.palette.text.disabled,
            boxShadow:
              '0px 2px 1px rgba(0, 0, 0, 0.05), 0px 0px 1px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      {
        props: { variant: 'outlined' },
        style: {
          background: theme.palette.common.white,
          color: theme.palette.text.primary,
          borderColor: theme.palette.primary.light,

          '&:hover': {
            background: theme.palette.common.white,
            borderColor: theme.palette.primary.light,
            boxShadow:
              'inset 0px 1px 4px rgba(0, 14, 62, 0.04), 0px 1px 2px rgba(77, 86, 120, 0.1)',
          },

          '&:focus': {
            borderColor: theme.palette.info.main,
          },

          '&.Mui-disabled': {
            backgroundColor: theme.palette.alabaster,
          },
        },
      },
    ],
  },

  MuiSwitch: {
    defaultProps: {
      disableRipple: false,
    },
    styleOverrides: {
      root: {
        width: 40,
        height: 24,
        padding: 0,

        '& .MuiSwitch-switchBase': {
          padding: 0,
          margin: 2,
          transitionDuration: '300ms',

          '&.Mui-checked': {
            transform: 'translateX(16px)',

            '& + .MuiSwitch-track': {
              backgroundColor: theme.palette.primary.main,
              opacity: 1,
            },
          },
        },

        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: 20,
          height: 20,
          color: theme.palette.common.white,
          border: '1px solid',
          borderColor: theme.palette.primary.light,
        },

        '& .MuiSwitch-track': {
          borderRadius: 52,
          border: '1px solid',
          borderColor: theme.palette.primary.light,
          boxShadow: 'inset 0px 2px 8px rgba(0, 0, 0, 0.08)',
        },
      },
    },
  },

  MuiInputBase: {
    styleOverrides: {
      root: {
        border: '1px solid',
        borderColor: theme.palette.primary.light,
        backgroundColor: theme.palette.alabaster,
        flex: 'auto',
        height: theme.spacing(6),
        borderRadius: '4px',
        color: theme.palette.text.primary,
        // eslint-disable-next-line unicorn/consistent-destructuring
        ...theme.typography.caption1,

        '&.Mui-focused': {
          borderColor: theme.palette.carribeanGreen,
        },

        '&.Mui-error': {
          borderColor: theme.palette.alabaster,
          backgroundColor: theme.palette.error.light,
          boxShadow:
            'inset 0px 1px 4px rgba(0, 14, 62, 0.04), 0px 1px 2px rgba(77, 86, 120, 0.1)',
        },
      },
      adornedStart: {
        paddingLeft: theme.spacing(1.5),
      },
      adornedEnd: {
        paddingRight: theme.spacing(2),
      },
      inputAdornedStart: {
        paddingLeft: theme.spacing(1),
      },
      input: {
        boxSizing: 'border-box',
        height: '100%',
        padding: theme.spacing(1.5, 1.5, 1.5, 2),

        '&::placeholder': {
          color: theme.palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },

  MuiInputLabel: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
      },
    },
  },
}
